'use client';

import { styled } from '@linaria/react';
import { memo, useEffect, useRef, useState, useContext } from 'react';
import { PopTopicCategories, GTMConstants } from '@fe-news/constants/pop-topic/type';
import { PopTopicTags } from '@fe-news/constants/pop-topic/default-value';
import Image from 'next/image';
import debounce from 'lodash.debounce';
import { PopTopicContext } from '@fe-news/context/pop-topic-provider';
import { CustomEventName, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';

const Container = styled.div`
  position: relative;
`;

const Ul = styled.ul`
  display: flex;
  align-items: center;
  margin-top: 10px;
  flex-wrap: nowrap;
  overflow-x: scroll;
  min-width: 0;

  scrollbar-width: none;

  &::-webkit-scrollbar,
  &::-webkit-scrollbar-track {
    display: none;
  }
`;

const Li = styled.li<{ active: boolean }>`
  cursor: pointer;
  display: flex;

  &:hover {
    color: #e03f19;
  }

  &:not(:last-child)::after {
    content: '';
    width: 2px;
    height: 16px;
    background-color: #e2e8f1;
    margin: 0 8px;
    display: inline-block;
    vertical-align: middle;
  }

  button {
    font-size: 16px;
    color: ${({ active }) => (active ? '#e03f19' : '#848d97')};
    font-weight: ${({ active }) => (active ? 500 : 'normal')};
    white-space: nowrap;
    cursor: pointer;
  }
`;

const ArrowTouch = styled.button<{ right?: boolean }>`
  position: absolute;
  top: -5px;
  padding: 0 8px 0 16px;
  z-index: 2;
  background-image: ${({ right }) =>
    right
      ? 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, #fff 50%)'
      : 'linear-gradient(to left, rgba(255, 255, 255, 0) 0%, #fff 50%)'};
  cursor: pointer;
`;

const NavBar = () => {
  const { category, setCategory, setKeyword } = useContext(PopTopicContext);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const elementReference = useRef<HTMLUListElement | null>(null);

  const onClickCategory = (category: PopTopicCategories) => {
    setCategory && setCategory(category);
    setKeyword && setKeyword('');
  };

  const handleScroll = debounce(() => {
    const element = elementReference.current;

    if (element) {
      const canScrollLeft = element.scrollLeft > 0;
      setCanScrollLeft(canScrollLeft);
      const canScrollRight = element.scrollLeft + element.clientWidth + 1 < element.scrollWidth;

      setCanScrollRight(canScrollRight);
    }
  }, 500);

  useEffect(() => {
    const element = elementReference.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      const canScrollRight = element.scrollLeft + element.clientWidth < element.scrollWidth;

      setCanScrollRight(canScrollRight);
    }

    return () => {
      if (element) {
        element.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  const onClick = (direction: 'left' | 'right') => {
    if (!elementReference.current) return;
    if (direction === 'left') {
      elementReference.current.scrollLeft -= 50;
    }
    if (direction === 'right') {
      elementReference.current.scrollLeft += 50;
    }
  };

  return (
    <Container>
      {canScrollLeft && (
        <ArrowTouch style={{ left: -8 }} onClick={() => onClick('left')}>
          <Image src='/assets/icons/icon-arrow-left.svg' alt='arrow back' width={24} height={24} />
        </ArrowTouch>
      )}
      <Ul ref={elementReference}>
        {Object.values(PopTopicCategories)
          .filter(
            name =>
              ![
                PopTopicCategories.ADVERTORIAL,
                PopTopicCategories.ANNOUNCEMENT,
                PopTopicCategories.ANUE_LIVE,
                PopTopicCategories.BC_LIVE,
                PopTopicCategories.CELEBRITY_AREA,
                PopTopicCategories.TW_CALC,
                PopTopicCategories.MSG,
                PopTopicCategories.ANNOUNCEMENT,
                PopTopicCategories.REPORT
              ].includes(name)
          )
          ?.map(name => {
            const onButtonClick = () => {
              onClickCategory(PopTopicTags[name].slug);
              sendGTM({
                [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_TAB,
                [GTMEventKey.SECTION]: GTMConstants.NEWS_CATEGORY_POP_TOPIC,
                [GTMEventKey.CLICK_ITEM]: `${PopTopicTags[name].name}`
              });
            };

            return (
              <Li key={name} active={PopTopicTags[name].slug === category}>
                <button onClick={onButtonClick}>{PopTopicTags[name].name}</button>
              </Li>
            );
          })}
      </Ul>
      {canScrollRight && (
        <ArrowTouch style={{ right: 0 }} onClick={() => onClick('right')} right>
          <Image src='/assets/icons/icon-arrow-right.svg' alt='arrow next' width={24} height={24} />
        </ArrowTouch>
      )}
    </Container>
  );
};

export default memo(NavBar);
